'use client';

import React, { useEffect, useRef, useCallback } from 'react';

/* ─── Config ─────────────────────────────────────────────────────────────── */
const CFG = {
  symbol: 'Gold',
  digits: 2,
  contract: 100,
  spread: 0.28,
  volume: 0.10,
  entry: 4608.02,
  tp: 4626.52,
  sl: 4599.52,
  balance: 10000,
  currency: 'USD',
  leverage: 100,
  ticket: '278451903',
  waNumber: '447452333665',
  waText: 'Hi, i am interested to learn this',
  ctaImage: "/assets/images/Profile_whatsapp-1788156814569.webp",
  ctaMessage: 'Want to learn how this works? I will walk you through it.',
  ctaButton: 'Message Me',
  ctaFine: 'Demo account illustration. Trading is risky; most retail accounts lose money.',
  ctaDelay: 0.70,
  refW: 390,
  refH: 720,
  scaleMin: 0.70,
  scaleMax: 1.70
};

const T_ENTRY = 1.15;
const T_CLOSE = 4.85;
const LOOP_END = 8.20;
const CANDLE_SEC = 0.32;

const OFF: [number, number][] = [
[0.00, 0.35], [0.60, -0.20], [T_ENTRY, 0.00], [1.75, -0.80],
[2.30, 2.75], [2.85, 6.35], [3.25, 4.95], [3.95, 11.85],
[T_CLOSE, 18.50]];


function r2(v: number, d: number) {const m = Math.pow(10, d);return Math.round(v * m) / m;}
const PATH = OFF.map((o) => ({ t: o[0], p: r2(CFG.entry + o[1], CFG.digits) }));
const smooth = (x: number) => x * x * (3 - 2 * x);

function pathAt(t: number) {
  if (t <= PATH[0].t) return PATH[0].p;
  const last = PATH[PATH.length - 1];
  if (t >= last.t) return last.p;
  for (let i = 0; i < PATH.length - 1; i++) {
    const a = PATH[i],b = PATH[i + 1];
    if (t >= a.t && t <= b.t) return a.p + (b.p - a.p) * smooth((t - a.t) / (b.t - a.t));
  }
  return last.p;
}
function noiseAmp(t: number) {
  const near = (x: number, w: number) => Math.max(0, 1 - Math.abs(t - x) / w);
  return 0.42 * (1 - Math.max(near(T_ENTRY, 0.30), near(T_CLOSE, 0.45)));
}
function priceAt(t: number) {
  const n = 0.55 * Math.sin(t * 13.1) + 0.30 * Math.sin(t * 31.7 + 1.3) + 0.15 * Math.sin(t * 53.3 + 2.1);
  return r2(pathAt(t) + n * noiseAmp(t), CFG.digits);
}

function seedRand(seed: number) {
  let s = seed;
  return function () {s = (s * 1664525 + 1013904223) % 4294967296;return s / 4294967296;};
}

const HIST = (() => {
  const rnd = seedRand(20260827),out: {o: number;h: number;l: number;c: number;}[] = [];
  let p = PATH[0].p - 9.0;
  for (let i = 0; i < 60; i++) {
    const o = p,drift = (PATH[0].p - p) * 0.07;
    const c = o + drift + (rnd() - 0.5) * 2.3;
    out.push({ o: r2(o, 2), h: r2(Math.max(o, c) + rnd() * 1.1, 2), l: r2(Math.min(o, c) - rnd() * 1.1, 2), c: r2(c, 2) });
    p = c;
  }
  out[out.length - 1].c = PATH[0].p;
  return out;
})();

const fmt = (v: number, d = CFG.digits) => v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const money = (v: number) => (v < 0 ? '-' : '') + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const plText = (v: number) => (v < 0 ? '-' : '+') + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ─── Component ──────────────────────────────────────────────────────────── */
export default function HowItWorksPage() {
  const appRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);

  // Mutable state refs (avoid re-renders in animation loop)
  const S = useRef({ t: 0, start: 0, price: CFG.entry, scale: 1, open: false, closed: false, profit: 0, balance: CFG.balance, paused: false });
  const candles = useRef<{o: number;h: number;l: number;c: number;}[]>([]);
  const liveIdx = useRef(-1);
  const yLo = useRef(0);
  const yHi = useRef(0);
  const yInit = useRef(false);
  const fired = useRef<boolean[]>([]);

  // DOM refs
  const stripRef = useRef<HTMLDivElement>(null);
  const stSymRef = useRef<HTMLSpanElement>(null);
  const stVolRef = useRef<HTMLSpanElement>(null);
  const stPlRef = useRef<HTMLDivElement>(null);
  const stPxRef = useRef<HTMLSpanElement>(null);
  const stTicketRef = useRef<HTMLSpanElement>(null);
  const fabRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const toastRef = useRef<HTMLDivElement>(null);
  const toastTRef = useRef<HTMLDivElement>(null);
  const toastMRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const tapdotRef = useRef<HTMLDivElement>(null);
  const dBuyRef = useRef<HTMLDivElement>(null);
  const dSellRef = useRef<HTMLDivElement>(null);
  const chOhlcRef = useRef<HTMLSpanElement>(null);
  const btnBuyRef = useRef<HTMLDivElement>(null);
  const fVolRef = useRef<HTMLSpanElement>(null);
  const fSlRef = useRef<HTMLSpanElement>(null);
  const fTpRef = useRef<HTMLSpanElement>(null);

  const resetCandles = useCallback(() => {
    candles.current = HIST.map((c) => ({ o: c.o, h: c.h, l: c.l, c: c.c }));
    liveIdx.current = -1;
    yInit.current = false;
  }, []);

  const updateCandles = useCallback((t: number, price: number) => {
    const idx = Math.floor(t / CANDLE_SEC);
    if (idx !== liveIdx.current) {liveIdx.current = idx;candles.current.push({ o: price, h: price, l: price, c: price });}
    const c = candles.current[candles.current.length - 1];
    c.c = price;
    if (price > c.h) c.h = price;
    if (price < c.l) c.l = price;
  }, []);

  const layout = useCallback(() => {
    const app = appRef.current;
    if (!app) return;
    const w = app.clientWidth,h = app.clientHeight;
    if (!w || !h) return;
    let s = Math.min(w / CFG.refW, h / CFG.refH);
    s = Math.max(CFG.scaleMin, Math.min(CFG.scaleMax, s));
    s = Math.round(s * 1000) / 1000;
    if (s !== S.current.scale) {S.current.scale = s;app.style.setProperty('--s', String(s));}
  }, []);

  const tap = useCallback((el: HTMLElement | null) => {
    if (!el) return;
    const app = appRef.current;
    const dot = tapdotRef.current;
    if (!app || !dot) return;
    const br = app.getBoundingClientRect(),r = el.getBoundingClientRect();
    dot.style.left = r.left - br.left + r.width / 2 + 'px';
    dot.style.top = r.top - br.top + r.height / 2 + 'px';
    dot.classList.remove('go');
    void dot.offsetWidth;
    dot.classList.add('go');
    el.classList.add('press');
    setTimeout(() => el.classList.remove('press'), 200);
  }, []);

  const showToast = useCallback((title: string, msg: string, accent?: string) => {
    if (toastTRef.current) toastTRef.current.textContent = title;
    if (toastMRef.current) toastMRef.current.innerHTML = msg;
    const t = toastRef.current;
    if (t) {t.style.borderLeftColor = accent || 'var(--green)';t.classList.add('on');}
  }, []);

  const hideToast = useCallback(() => {toastRef.current?.classList.remove('on');}, []);

  const drawChart = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const cx = cv.getContext('2d');
    if (!cx) return;
    let s = S.current.scale;
    const stripH = stripRef.current?.offsetHeight || 62 * s;
    const PAD_R = Math.round(62 * s),PAD_T = Math.round(12 * s);
    const PAD_B = Math.round(stripH + 22 * s);
    const cw = cv.offsetWidth,ch = cv.offsetHeight;
    if (!cw || !ch) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (cv.width !== Math.round(cw * dpr) || cv.height !== Math.round(ch * dpr)) {
      cv.width = Math.round(cw * dpr);cv.height = Math.round(ch * dpr);
      cx.setTransform(dpr, 0, 0, dpr, 0, 0);
      yInit.current = false;
    }
    const plotW = Math.max(60, cw - PAD_R);
    const plotH = Math.max(40, ch - PAD_T - PAD_B);
    const nWanted = Math.round(plotW / (11 * s));
    const n = Math.max(16, Math.min(60, nWanted));
    const view = candles.current.slice(-n);
    let lo = Infinity,hi = -Infinity;
    view.forEach((c) => {lo = Math.min(lo, c.l);hi = Math.max(hi, c.h);});
    if (S.current.open || S.current.closed) {lo = Math.min(lo, CFG.entry);hi = Math.max(hi, CFG.tp);}
    const pad = (hi - lo) * 0.14 || 1;lo -= pad;hi += pad;
    if (!yInit.current) {yLo.current = lo;yHi.current = hi;yInit.current = true;} else
    {yLo.current += (lo - yLo.current) * 0.18;yHi.current += (hi - yHi.current) * 0.18;}
    const Y = (p: number) => PAD_T + (yHi.current - p) / (yHi.current - yLo.current) * plotH;
    const step = plotW / view.length,bw = Math.max(2, step * 0.62);
    const fsAxis = Math.max(8, Math.round(10 * s));
    cx.clearRect(0, 0, cw, ch);
    cx.fillStyle = '#161b22';cx.fillRect(0, 0, cw, ch);
    cx.font = fsAxis + 'px -apple-system,Roboto,sans-serif';
    cx.textBaseline = 'middle';
    for (let i = 0; i <= 4; i++) {
      let p = yLo.current + (yHi.current - yLo.current) * i / 4,y = Y(p);
      cx.strokeStyle = 'rgba(255,255,255,.05)';
      cx.beginPath();cx.moveTo(0, y);cx.lineTo(plotW, y);cx.stroke();
      cx.fillStyle = '#6f7b8a';cx.fillText(fmt(p, 2), plotW + Math.round(7 * s), y);
    }
    if (S.current.open || S.current.closed) {
      const yE = Y(CFG.entry),yC = Y(S.current.price);
      cx.fillStyle = S.current.price >= CFG.entry ? 'rgba(63,185,80,.13)' : 'rgba(229,83,75,.13)';
      cx.fillRect(0, Math.min(yE, yC), plotW, Math.abs(yE - yC));
    }
    view.forEach((c, i) => {
      const x = i * step + step / 2,up = c.c >= c.o,col = up ? '#3fb950' : '#e5534b';
      cx.strokeStyle = col;cx.fillStyle = col;cx.lineWidth = Math.max(1, s * 0.9);
      cx.beginPath();cx.moveTo(Math.round(x) + .5, Y(c.h));cx.lineTo(Math.round(x) + .5, Y(c.l));cx.stroke();
      const yo = Y(c.o),yc = Y(c.c);
      cx.fillRect(x - bw / 2, Math.min(yo, yc), bw, Math.max(1.5, Math.abs(yo - yc)));
    });
    const chipH = Math.round(16 * s);
    const label = (p: number, color: string, text: string, dash?: number[]) => {
      const y = Y(p);
      cx.save();cx.setLineDash(dash || []);cx.strokeStyle = color;cx.lineWidth = Math.max(1, s * 0.9);
      cx.beginPath();cx.moveTo(0, y + .5);cx.lineTo(plotW, y + .5);cx.stroke();cx.restore();
      cx.fillStyle = color;cx.fillRect(plotW + 2, y - chipH / 2, PAD_R - 4, chipH);
      cx.fillStyle = '#fff';cx.font = '600 ' + fsAxis + 'px -apple-system,Roboto,sans-serif';
      cx.fillText(text, plotW + Math.round(5 * s), y + .5);
      cx.font = fsAxis + 'px -apple-system,Roboto,sans-serif';
    };
    if (S.current.open || S.current.closed) {
      label(CFG.entry, '#3b8de0', fmt(CFG.entry), [4, 3]);
      label(CFG.tp, '#3fb950', fmt(CFG.tp), [2, 3]);
      const idxEntry = Math.max(0, view.length - 1 - Math.floor((S.current.t - T_ENTRY) / CANDLE_SEC));
      const x = idxEntry * step + step / 2,y = Y(CFG.entry),a = Math.round(4.5 * s);
      cx.fillStyle = '#3b8de0';
      cx.beginPath();cx.moveTo(x, y - 2 * a);cx.lineTo(x - a, y - 1);cx.lineTo(x + a, y - 1);cx.closePath();cx.fill();
    }
    label(S.current.price, (S.current.open || S.current.closed) && S.current.price >= CFG.entry ? '#3fb950' : '#8b96a5', fmt(S.current.price), [1, 2]);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const frame = useCallback((now: number) => {
    if (S.current.paused) return;
    const t = (now - S.current.start) / 1000;
    S.current.t = t;
    if (t >= LOOP_END) {
      resetCandles();
      S.current.start = performance.now();
      fired.current = new Array(8).fill(false);
      rafRef.current = requestAnimationFrame(frame);
      return;
    }
    if (!S.current.closed) S.current.price = priceAt(t);
    updateCandles(t, S.current.price);

    // Events
    const EV: [number, () => void][] = [
    [0.00, () => {
      S.current.open = false;S.current.closed = false;S.current.profit = 0;S.current.balance = CFG.balance;
      hideToast();
      ctaRef.current?.classList.remove('on');
      sheetRef.current?.classList.remove('up');
      stripRef.current?.classList.remove('on', 'win', 'done');
      fabRef.current?.classList.remove('gone');
      if (stSymRef.current) stSymRef.current.textContent = CFG.symbol;
      if (stVolRef.current) stVolRef.current.textContent = 'buy ' + CFG.volume.toFixed(2);
      if (stPxRef.current) stPxRef.current.textContent = fmt(CFG.entry) + ' → ' + fmt(CFG.entry);
      if (stPlRef.current) stPlRef.current.textContent = plText(0);
      if (stTicketRef.current) stTicketRef.current.textContent = '#' + CFG.ticket;
      if (fVolRef.current) fVolRef.current.textContent = CFG.volume.toFixed(2);
      if (fSlRef.current) fSlRef.current.textContent = fmt(CFG.sl);
      if (fTpRef.current) fTpRef.current.textContent = fmt(CFG.tp);
    }],
    [0.25, () => {tap(fabRef.current);}],
    [0.50, () => {sheetRef.current?.classList.add('up');fabRef.current?.classList.add('gone');}],
    [0.90, () => {tap(btnBuyRef.current);}],
    [T_ENTRY, () => {
      S.current.open = true;
      sheetRef.current?.classList.remove('up');
      stripRef.current?.classList.add('on');
      showToast('Order placed', '#' + CFG.ticket + ' buy ' + CFG.volume.toFixed(2) + ' ' + CFG.symbol + ' at <b>' + fmt(CFG.entry) + '</b>', 'var(--blue)');
    }],
    [2.60, hideToast],
    [T_CLOSE, () => {
      S.current.price = CFG.tp;
      S.current.profit = r2((CFG.tp - CFG.entry) * CFG.contract * CFG.volume, 2);
      S.current.balance = r2(CFG.balance + S.current.profit, 2);
      S.current.open = false;S.current.closed = true;
      stripRef.current?.classList.add('win', 'done');
      if (stVolRef.current) stVolRef.current.textContent = 'buy ' + CFG.volume.toFixed(2) + ' · closed';
      if (stPxRef.current) stPxRef.current.textContent = fmt(CFG.entry) + ' → ' + fmt(CFG.tp);
      if (stPlRef.current) stPlRef.current.textContent = plText(S.current.profit);
      showToast('Position closed', 'Take Profit hit at <b>' + fmt(CFG.tp) + '</b> · <b class="green">+' + money(S.current.profit) + ' ' + CFG.currency + '</b><br>Balance <b>' + money(S.current.balance) + ' ' + CFG.currency + '</b>');
    }],
    [T_CLOSE + CFG.ctaDelay, () => {
      ctaRef.current?.classList.add('on');
      S.current.paused = true;
    }]];


    for (let i = 0; i < EV.length; i++) {
      if (!fired.current[i] && t >= EV[i][0]) {fired.current[i] = true;EV[i][1]();}
    }

    if (S.current.open) {
      S.current.profit = r2((S.current.price - CFG.entry) * CFG.contract * CFG.volume, 2);
      if (stPlRef.current) {
        stPlRef.current.textContent = plText(S.current.profit);
        stPlRef.current.className = 'pl ' + (S.current.profit >= 0 ? 'green' : 'red');
      }
      if (stPxRef.current) stPxRef.current.textContent = fmt(CFG.entry) + ' → ' + fmt(S.current.price);
      stripRef.current?.classList.toggle('win', S.current.profit >= 0);
    }

    if (dBuyRef.current) dBuyRef.current.textContent = fmt(r2(S.current.price + CFG.spread, CFG.digits));
    if (dSellRef.current) dSellRef.current.textContent = fmt(S.current.price);
    const c = candles.current[candles.current.length - 1];
    if (chOhlcRef.current) chOhlcRef.current.textContent = 'O ' + fmt(c.o) + '  H ' + fmt(c.h) + '  L ' + fmt(c.l) + '  C ' + fmt(c.c);
    drawChart();
    rafRef.current = requestAnimationFrame(frame);
  }, [drawChart, hideToast, resetCandles, showToast, tap, updateCandles]);

  const restart = useCallback(() => {
    S.current.paused = false;
    resetCandles();
    S.current.start = performance.now();
    fired.current = new Array(8).fill(false);
    rafRef.current = requestAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    layout();
    resetCandles();
    fired.current = new Array(8).fill(false);
    S.current.start = performance.now();
    rafRef.current = requestAnimationFrame(frame);

    const app = appRef.current;
    let ro: ResizeObserver | null = null;
    if (app && 'ResizeObserver' in window) {
      ro = new ResizeObserver(layout);
      ro.observe(app);
    }
    window.addEventListener('resize', layout);
    window.addEventListener('orientationchange', () => setTimeout(layout, 250));
    if (window.visualViewport) window.visualViewport.addEventListener('resize', layout);
    const onVis = () => {if (!document.hidden && !S.current.paused) restart();};
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro?.disconnect();
      window.removeEventListener('resize', layout);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [frame, layout, resetCandles, restart]);

  const handleCtaClose = () => {
    ctaRef.current?.classList.remove('on');
    restart();
  };

  return (
    <>
      <style>{`
        :root{
          --bg:#12161c;--panel:#1b2028;--panel-2:#222834;--bar:#2a313d;--line:#2f3743;
          --text:#e9edf3;--muted:#8b96a5;--blue:#3b8de0;--green:#3fb950;--red:#e5534b;--wa:#25d366;
        }
        .sim-app{
          --s:1;
          --fs-nano:calc(10px * var(--s));--fs-xs:calc(11.5px * var(--s));--fs-sm:calc(12.5px * var(--s));
          --fs-md:calc(13.5px * var(--s));--fs-lg:calc(15px * var(--s));--fs-xl:calc(17px * var(--s));
          --fs-pl:calc(27px * var(--s));--sp-1:calc(4px * var(--s));--sp-2:calc(6px * var(--s));
          --sp-3:calc(10px * var(--s));--sp-4:calc(13px * var(--s));--sp-5:calc(16px * var(--s));
          --sp-6:calc(20px * var(--s));--h-bar:calc(54px * var(--s));--h-tabs:calc(60px * var(--s));
          --ico:calc(21px * var(--s));--r-sm:calc(6px * var(--s));--r-md:calc(11px * var(--s));--r-lg:calc(17px * var(--s));
          position:fixed;left:0;top:0;width:100%;height:100vh;height:100svh;height:100dvh;
          display:flex;flex-direction:column;overflow:hidden;background:var(--bg);color:var(--text);
          font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;
          -webkit-text-size-adjust:100%;box-sizing:border-box;
          padding-top:env(safe-area-inset-top,0px);
        }
        .sim-app *{box-sizing:border-box;-webkit-tap-highlight-color:transparent}
        .sim-appbar{
          height:var(--h-bar);flex:none;background:var(--bar);display:flex;align-items:center;
          padding:0 var(--sp-4);gap:var(--sp-4);border-bottom:1px solid rgba(0,0,0,.35);
        }
        .sim-hamb{display:flex;flex-direction:column;gap:calc(3.5px*var(--s));width:calc(18px*var(--s));flex:none}
        .sim-hamb i{height:calc(2px*var(--s));background:var(--text);border-radius:2px;display:block}
        .sim-title{font-size:var(--fs-xl);font-weight:600;flex:1;line-height:1.15;min-width:0}
        .sim-title small{display:block;font-size:var(--fs-xs);font-weight:500;color:var(--muted);margin-top:calc(2px*var(--s));letter-spacing:.2px}
        .sim-acts{display:flex;gap:var(--sp-5);color:var(--muted);font-size:var(--fs-xl);flex:none}
        .sim-tfbar{display:flex;gap:var(--sp-2);padding:var(--sp-3) var(--sp-4);background:var(--panel);border-bottom:1px solid var(--line);flex:none;overflow:hidden}
        .sim-tf{font-size:var(--fs-sm);font-weight:600;color:var(--muted);padding:var(--sp-1) var(--sp-3);border-radius:var(--r-sm);background:var(--panel-2);white-space:nowrap}
        .sim-tf.on{background:var(--blue);color:#fff}
        .sim-chartwrap{flex:1;position:relative;background:#161b22;min-height:0}
        .sim-canvas{position:absolute;inset:0;width:100%;height:100%;display:block}
        .sim-charthead{position:absolute;top:var(--sp-3);left:var(--sp-4);font-size:var(--fs-sm);color:var(--muted);font-variant-numeric:tabular-nums;line-height:1.5;pointer-events:none}
        .sim-charthead b{color:var(--text);font-size:var(--fs-md);letter-spacing:.3px}
        .sim-fab{
          position:absolute;right:var(--sp-5);bottom:var(--sp-5);
          width:calc(54px*var(--s));height:calc(54px*var(--s));border-radius:50%;background:var(--blue);
          display:flex;align-items:center;justify-content:center;
          box-shadow:0 6px 18px rgba(59,141,224,.45);transition:transform .12s,opacity .2s;cursor:pointer;
        }
        .sim-fab.press{transform:scale(.88)}
        .sim-fab.gone{opacity:0;transform:scale(.6)}
        .sim-fab svg{width:calc(26px*var(--s));height:calc(26px*var(--s));fill:#fff}
        .sim-strip{
          position:absolute;left:var(--sp-3);right:var(--sp-3);bottom:var(--sp-4);z-index:20;
          background:rgba(27,32,40,.94);border:1px solid var(--line);border-left:3px solid var(--blue);
          border-radius:var(--r-md);padding:var(--sp-3) var(--sp-4);backdrop-filter:blur(6px);
          box-shadow:0 10px 30px rgba(0,0,0,.45);
          opacity:0;transform:translateY(16px) scale(.97);
          transition:opacity .22s ease,transform .28s cubic-bezier(.2,.85,.3,1),border-color .3s,background .3s;
        }
        .sim-strip.on{opacity:1;transform:none}
        .sim-strip.win{border-left-color:var(--green);background:rgba(28,44,33,.94)}
        .sim-strip.done{animation:simPop .5s cubic-bezier(.2,.9,.3,1)}
        @keyframes simPop{0%{transform:scale(1)}35%{transform:scale(1.055)}100%{transform:scale(1)}}
        .sim-strip .r1{display:flex;justify-content:space-between;align-items:baseline;gap:var(--sp-3)}
        .sim-strip .sym{font-size:var(--fs-lg);font-weight:600;letter-spacing:.2px;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .sim-strip .sym u{text-decoration:none;color:var(--muted);font-weight:500;font-size:var(--fs-sm);margin-left:var(--sp-2)}
        .sim-strip .pl{font-size:var(--fs-pl);font-weight:700;font-variant-numeric:tabular-nums;letter-spacing:-.3px;line-height:1;white-space:nowrap;flex:none}
        .sim-strip .r2{display:flex;justify-content:space-between;gap:var(--sp-3);margin-top:var(--sp-2);font-size:var(--fs-sm);color:var(--muted);font-variant-numeric:tabular-nums;white-space:nowrap}
        .sim-strip .r2 s{text-decoration:none;color:#c3cbd6}
        .green{color:var(--green)}.red{color:var(--red)}
        .sim-tabbar{
          height:calc(var(--h-tabs) + env(safe-area-inset-bottom,0px));flex:none;background:var(--bar);
          display:flex;border-top:1px solid rgba(0,0,0,.4);padding-bottom:env(safe-area-inset-bottom,0px);
        }
        .sim-tab{flex:1;min-width:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:calc(3px*var(--s));color:var(--muted);font-size:var(--fs-nano);font-weight:500}
        .sim-tab svg{width:var(--ico);height:var(--ico);fill:currentColor}
        .sim-tab.on{color:var(--blue)}
        .sim-sheet{
          position:absolute;left:0;right:0;bottom:0;background:var(--panel);z-index:25;
          border-radius:var(--r-lg) var(--r-lg) 0 0;max-height:78%;overflow:hidden;
          transform:translateY(102%);transition:transform .26s cubic-bezier(.2,.85,.3,1);
          box-shadow:0 -12px 40px rgba(0,0,0,.5);padding-bottom:env(safe-area-inset-bottom,0px);
        }
        .sim-sheet.up{transform:none}
        .sim-grab{width:calc(38px*var(--s));height:calc(4px*var(--s));border-radius:3px;background:#414b59;margin:var(--sp-3) auto 0}
        .sim-sh{display:flex;align-items:center;justify-content:space-between;padding:var(--sp-4) var(--sp-5) var(--sp-3);border-bottom:1px solid var(--line)}
        .sim-sh b{font-size:var(--fs-lg)}
        .sim-sh span{font-size:var(--fs-md);color:var(--blue)}
        .sim-field{display:flex;align-items:center;justify-content:space-between;gap:var(--sp-3);padding:var(--sp-3) var(--sp-5);border-bottom:1px solid var(--line)}
        .sim-field .k{font-size:var(--fs-md);color:var(--muted)}
        .sim-field .v{font-size:var(--fs-lg);font-weight:600;font-variant-numeric:tabular-nums}
        .sim-dealbtns{display:flex;gap:var(--sp-3);padding:var(--sp-4) var(--sp-5) var(--sp-6)}
        .sim-deal{flex:1;min-width:0;border-radius:var(--r-md);padding:var(--sp-3) var(--sp-2);text-align:center;transition:transform .12s,filter .12s;cursor:pointer}
        .sim-deal .t{font-size:var(--fs-sm);font-weight:700;letter-spacing:1.1px;opacity:.85}
        .sim-deal .p{font-size:calc(20px*var(--s));font-weight:600;font-variant-numeric:tabular-nums;margin-top:calc(2px*var(--s))}
        .sim-deal.sell{background:var(--red)}
        .sim-deal.buy{background:var(--blue)}
        .sim-deal.press{transform:scale(.95);filter:brightness(1.3)}
        .sim-toast{
          position:absolute;left:var(--sp-3);right:var(--sp-3);top:var(--sp-3);z-index:35;background:#2f3a46;
          border-left:3px solid var(--green);border-radius:var(--r-md);padding:var(--sp-3) var(--sp-4);
          box-shadow:0 10px 30px rgba(0,0,0,.5);
          opacity:0;transform:translateY(-14px);transition:opacity .2s,transform .26s cubic-bezier(.2,.85,.3,1);
        }
        .sim-toast.on{opacity:1;transform:none}
        .sim-toast .t{font-size:var(--fs-xs);color:var(--muted);margin-bottom:var(--sp-1);letter-spacing:.3px}
        .sim-toast .m{font-size:var(--fs-md);line-height:1.4;font-variant-numeric:tabular-nums}
        .sim-tapdot{
          position:absolute;width:calc(44px*var(--s));height:calc(44px*var(--s));
          margin:calc(-22px*var(--s)) 0 0 calc(-22px*var(--s));z-index:60;pointer-events:none;opacity:0;
        }
        .sim-tapdot::before{content:"";position:absolute;inset:20%;border-radius:50%;background:rgba(255,255,255,.55)}
        .sim-tapdot::after{content:"";position:absolute;inset:0;border-radius:50%;border:2px solid rgba(255,255,255,.75);transform:scale(.4);opacity:0}
        .sim-tapdot.go{animation:simDotIn .42s ease forwards}
        .sim-tapdot.go::after{animation:simRipple .42s ease forwards}
        @keyframes simDotIn{0%{opacity:0;transform:scale(.7)}25%{opacity:1;transform:scale(1)}100%{opacity:0;transform:scale(1)}}
        @keyframes simRipple{0%{transform:scale(.4);opacity:.9}100%{transform:scale(1.5);opacity:0}}
        .sim-cta{
          position:absolute;inset:0;z-index:80;display:flex;align-items:center;justify-content:center;
          padding:var(--sp-6);background:rgba(6,9,13,.72);backdrop-filter:blur(4px);
          opacity:0;pointer-events:none;transition:opacity .3s ease;
        }
        .sim-cta.on{opacity:1;pointer-events:auto}
        .sim-cta-card{
          width:100%;max-width:calc(320px*var(--s));max-height:100%;overflow-y:auto;
          background:var(--panel);border:1px solid var(--line);border-radius:calc(18px*var(--s));
          padding:calc(22px*var(--s)) var(--sp-6) var(--sp-5);text-align:center;position:relative;
          box-shadow:0 24px 60px rgba(0,0,0,.6);
          transform:translateY(18px) scale(.96);transition:transform .34s cubic-bezier(.2,.9,.3,1);
          cursor:pointer;
        }
        .sim-cta.on .sim-cta-card{transform:none}
        .sim-cta-x{
          position:absolute;top:var(--sp-2);right:var(--sp-2);
          width:calc(30px*var(--s));height:calc(30px*var(--s));border:0;border-radius:50%;
          background:transparent;color:var(--muted);font-size:calc(20px*var(--s));line-height:1;cursor:pointer;
        }
        .sim-cta-x:hover{background:rgba(255,255,255,.07);color:var(--text)}
        .sim-cta-img{
          width:calc(108px*var(--s));height:calc(108px*var(--s));border-radius:50%;object-fit:cover;
          display:block;margin:calc(2px*var(--s)) auto var(--sp-4);
          border:3px solid var(--panel-2);box-shadow:0 6px 20px rgba(0,0,0,.45);
        }
        .sim-cta-msg{margin:0 0 var(--sp-5);font-size:calc(18px*var(--s));font-weight:600;line-height:1.35;color:#eef2f7;letter-spacing:-.1px}
        .sim-cta-btn{
          display:flex;align-items:center;justify-content:center;gap:var(--sp-3);
          background:var(--wa);color:#06301a;text-decoration:none;
          font-size:calc(15.5px*var(--s));font-weight:700;
          padding:var(--sp-4) var(--sp-5);border-radius:var(--r-md);letter-spacing:.2px;
          box-shadow:0 6px 18px rgba(37,211,102,.32);transition:filter .15s,transform .15s;
        }
        .sim-cta-btn:hover{filter:brightness(1.06);transform:translateY(-1px)}
        .sim-cta-btn svg{width:calc(20px*var(--s));height:calc(20px*var(--s));fill:#06301a;flex:none}
        .sim-cta-fine{margin:var(--sp-4) 0 0;font-size:var(--fs-nano);line-height:1.45;color:#6f7b8a}
        @media (max-height:430px){
          .sim-tfbar{display:none}
          .sim-cta-img{width:calc(76px*var(--s));height:calc(76px*var(--s));margin-bottom:var(--sp-3)}
          .sim-cta-fine{display:none}
        }
        @media (prefers-reduced-motion:reduce){
          .sim-sheet,.sim-toast,.sim-strip,.sim-cta,.sim-cta-card{transition-duration:.01ms}
        }
      `}</style>

      <div className="sim-app" ref={appRef}>
        {/* App Bar */}
        <div className="sim-appbar">
          <span className="sim-hamb"><i /><i /><i /></span>
          <span className="sim-title">Charts<small>Demo account · 1:100</small></span>
          <span className="sim-acts">＋ ⚙</span>
        </div>

        {/* Timeframe Bar */}
        <div className="sim-tfbar">
          {['M1', 'M5', 'M15', 'M30', 'H1', 'H4', 'D1'].map((tf) =>
          <span key={tf} className={`sim-tf${tf === 'M5' ? ' on' : ''}`}>{tf}</span>
          )}
        </div>

        {/* Chart Area */}
        <div className="sim-chartwrap">
          <canvas ref={canvasRef} className="sim-canvas" />
          <div className="sim-charthead">
            <b>Gold, M5</b><br />
            <span ref={chOhlcRef}></span>
          </div>

          {/* FAB */}
          <div className="sim-fab" ref={fabRef}>
            <svg viewBox="0 0 24 24"><path d="M11 5h2v14h-2z" /><path d="M5 11h14v2H5z" /></svg>
          </div>

          {/* Trade Strip */}
          <div className="sim-strip" ref={stripRef}>
            <div className="r1">
              <div className="sym">
                <span ref={stSymRef}>Gold</span>
                <u ref={stVolRef}>buy 0.10</u>
              </div>
              <div className="pl green" ref={stPlRef}>+$0.00</div>
            </div>
            <div className="r2">
              <span><s ref={stPxRef}></s></span>
              <span ref={stTicketRef}>#278451903</span>
            </div>
          </div>

          {/* Order Sheet */}
          <div className="sim-sheet" ref={sheetRef}>
            <div className="sim-grab" />
            <div className="sim-sh"><b>Gold</b><span>Market Execution</span></div>
            <div className="sim-field"><span className="k">Volume, lots</span><span className="v" ref={fVolRef}>0.10</span></div>
            <div className="sim-field"><span className="k">Stop Loss</span><span className="v" ref={fSlRef}></span></div>
            <div className="sim-field"><span className="k">Take Profit</span><span className="v" ref={fTpRef}></span></div>
            <div className="sim-dealbtns">
              <div className="sim-deal sell"><div className="t">SELL</div><div className="p" ref={dSellRef}>—</div></div>
              <div className="sim-deal buy" ref={btnBuyRef}><div className="t">BUY</div><div className="p" ref={dBuyRef}>—</div></div>
            </div>
          </div>

          {/* Toast */}
          <div className="sim-toast" ref={toastRef}>
            <div className="t" ref={toastTRef}></div>
            <div className="m" ref={toastMRef}></div>
          </div>

          {/* Tap Dot */}
          <div className="sim-tapdot" ref={tapdotRef} />

          {/* CTA Overlay */}
          <div className="sim-cta" ref={ctaRef}>
            <div
              className="sim-cta-card"
              onClick={(e) => {
                const target = e.target as HTMLElement;
                if (target.closest('.sim-cta-x')) return;
                window.open(`https://wa.me/${CFG.waNumber}?text=${encodeURIComponent(CFG.waText)}`, '_blank', 'noopener,noreferrer');
              }}
            >
              <button className="sim-cta-x" onClick={handleCtaClose} aria-label="Close">×</button>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                className="sim-cta-img"
                src={CFG.ctaImage}
                alt="Forex trading instructor" />
              
              <p className="sim-cta-msg">{CFG.ctaMessage}</p>
              <a
                className="sim-cta-btn"
                href={`https://wa.me/${CFG.waNumber}?text=${encodeURIComponent(CFG.waText)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}>
                
                <svg viewBox="0 0 24 24">
                  <path d="M17.5 14.4c-.3-.2-1.7-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.7 1-.9 1.2-.2.2-.3.2-.6.1-1.7-.9-2.9-1.6-4-3.6-.3-.5.3-.5.9-1.6.1-.2 0-.4 0-.5 0-.2-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.2.2 2.1 3.2 5.1 4.5 1.9.8 2.6.9 3.5.7.6-.1 1.7-.7 1.9-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.6-.4z" />
                  <path d="M12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.4 1.3 4.9L2 22l5.3-1.3C8.7 21.5 10.3 22 12 22c5.5 0 10-4.5 10-10S17.5 2 12 2zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3.1.8.8-3-.2-.3C4.1 15 3.7 13.5 3.7 12c0-4.6 3.7-8.3 8.3-8.3s8.3 3.7 8.3 8.3-3.7 8.2-8.3 8.2z" />
                </svg>
                <span>{CFG.ctaButton}</span>
                <svg viewBox="0 0 24 24" style={{width:'calc(18px*var(--s))',height:'calc(18px*var(--s))',fill:'#06301a',flex:'none'}}>
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/>
                </svg>
              </a>
              <p className="sim-cta-fine">{CFG.ctaFine}</p>
            </div>
          </div>
        </div>

        {/* Tab Bar */}
        <div className="sim-tabbar">
          <div className="sim-tab">
            <svg viewBox="0 0 24 24"><path d="M4 18h3v-5H4v5zm6.5 0h3V6h-3v12zM17 18h3v-8h-3v8z" /></svg>
            Quotes
          </div>
          <div className="sim-tab on">
            <svg viewBox="0 0 24 24"><path d="M3.5 17.2 9 11.7l3.4 3.4 6.3-7-1.5-1.3-4.8 5.4L9 8.8l-6.9 6.9z" /><path d="M3 19h18v1.6H3z" /></svg>
            Charts
          </div>
          <div className="sim-tab">
            <svg viewBox="0 0 24 24"><path d="M7 3 3 7.5h3V16h2V7.5h3L7 3zm10 18 4-4.5h-3V8h-2v8.5h-3l4 4.5z" /></svg>
            Trade
          </div>
          <div className="sim-tab">
            <svg viewBox="0 0 24 24"><path d="M12 4a8 8 0 1 0 7.7 10.2l-1.9-.6A6 6 0 1 1 12 6v3l4-4-4-4v3z" /><path d="M11.2 8h1.6v4.6l3.4 2-.8 1.4-4.2-2.4z" /></svg>
            History
          </div>
          <div className="sim-tab">
            <svg viewBox="0 0 24 24"><path d="M4 4h16v12H7.5L4 19.5V4zm2.5 4.5v1.8h11V8.5h-11zm0 3.4v1.8h7.5v-1.8H6.5z" /></svg>
            Messages
          </div>
        </div>
      </div>
    </>
  );

}